import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase, configured } from './supabase'
import { CANCELLED, passkeyCancelled, passkeySignInError } from './passkeyError'
import { onAuthUser, unsentChangeCount } from './store'
import { authLinkFromHash } from './authLink'
import { Banner } from '../ui/Banner'

interface AuthValue {
  session: Session | null
  loading: boolean
  signInWithPasskey: () => Promise<string | null>
  signInWithPassword: (email: string, password: string) => Promise<string | null>
  registerPasskey: () => Promise<string | null>
  /** U0.6: an invite or recovery link landed. `password` until one is set,
   *  then `passkey` while the offer is open, then null and the app draws. */
  setup: 'password' | 'passkey' | null
  /** An expired or used email link, for the sign-in screen to say. */
  linkError: string | null
  setPassword: (password: string) => Promise<string | null>
  finishSetup: () => void
  signOut: () => Promise<void>
  /** U5.2: delete this account for good. Resolves to an error string, or null
   *  once the Worker has deleted it and this device has signed out. */
  deleteAccount: (confirmEmail: string) => Promise<string | null>
}

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined)?.replace(/\/+$/, '')

const Ctx = createContext<AuthValue | null>(null)

const b64urlToBytes = (s: string) => {
  const pad = s.replace(/-/g, '+').replace(/_/g, '/')
  const bin = atob(pad + '='.repeat((4 - (pad.length % 4)) % 4))
  return Uint8Array.from(bin, c => c.charCodeAt(0))
}

const bytesToB64url = (b: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(b)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

/** Prefer the browser's own parser; hand-roll where it is missing.
 *  The server sends challenge, user.id and excludeCredentials ids as
 *  base64url strings, and the WebAuthn API wants ArrayBuffers. */
function parseCreationOptions(json: any): PublicKeyCredentialCreationOptions {
  const P = PublicKeyCredential as unknown as {
    parseCreationOptionsFromJSON?: (j: any) => PublicKeyCredentialCreationOptions
  }
  if (typeof P.parseCreationOptionsFromJSON === 'function') {
    return P.parseCreationOptionsFromJSON(json)
  }
  return {
    ...json,
    challenge: b64urlToBytes(json.challenge),
    user: { ...json.user, id: b64urlToBytes(json.user.id) },
    excludeCredentials: (json.excludeCredentials ?? []).map((c: any) => ({
      ...c,
      id: b64urlToBytes(c.id),
    })),
  } as PublicKeyCredentialCreationOptions
}

function toJSON(credential: PublicKeyCredential): any {
  const withToJSON = credential as PublicKeyCredential & { toJSON?: () => unknown }
  if (typeof withToJSON.toJSON === 'function') return withToJSON.toJSON()
  const response = credential.response as AuthenticatorAttestationResponse
  return {
    id: credential.id,
    rawId: bytesToB64url(credential.rawId),
    type: 'public-key',
    response: {
      attestationObject: bytesToB64url(response.attestationObject),
      clientDataJSON: bytesToB64url(response.clientDataJSON),
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
  }
}

// Read once, at load, before supabase-js has cleared the hash: by the time any
// effect runs it has turned the token into a session and the hash is gone.
// This module's body runs in the same task as `supabase.ts`'s `createClient`,
// whose URL handling is behind an await, so the hash is still here.
const LINK = typeof location === 'undefined' ? { kind: null, error: null } : authLinkFromHash(location.hash)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  // Set when a sign-out was asked for while writes were still queued. The
  // queue is cleared on sign-out (it is the signed-out user's), so the person
  // is told once; the next sign-out goes through.
  const [unsent, setUnsent] = useState<number | null>(null)
  // U0.6: a person who arrived by an invite or recovery link has a session
  // and no password. Held here, above the session gate in `App`, so Today
  // does not draw for them first.
  const [setup, setSetup] = useState<'password' | 'passkey' | null>(LINK.kind ? 'password' : null)

  useEffect(() => {
    if (!configured) { setLoading(false); return }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
    })
    // U0.4: the store learns who is here from the same event the context does,
    // so a sign-out or a different user's sign-in clears the last user's
    // cache and queued writes in one place, before any screen re-renders.
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      onAuthUser(event, s?.user.id ?? null)
      // A recovery link announces itself; an invite is a plain SIGNED_IN and
      // was caught from the hash at load.
      if (event === 'PASSWORD_RECOVERY') setSetup(v => v ?? 'password')
      setSession(s)
      if (!s) setUnsent(null)
    })
    return () => sub.subscription.unsubscribe()
  }, [])

  // Every method returns an error string or null, so callers render the message
  // rather than each one inventing its own failure handling.
  const value: AuthValue = {
    session,
    loading,
    async signInWithPasskey() {
      const { error } = await supabase.auth.signInWithPasskey()
      if (error && !passkeyCancelled(error)) console.warn('passkey sign-in failed', error)
      return passkeySignInError(error)
    },
    async signInWithPassword(email, password) {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      return error?.message ?? null
    },
    async registerPasskey() {
      // The two-step API rather than `auth.registerPasskey()`, which does the
      // whole ceremony itself and takes no options but an AbortSignal.
      //
      // What the server sends is `residentKey: required, userVerification:
      // preferred` and no authenticatorAttachment. That *permits* Face ID and
      // does not *ask* for it: with no attachment stated, iOS is free to lead
      // with "use a passkey from another device" or a security key, and
      // "preferred" lets it skip the biometric check altogether. Asking for
      // `platform` and `required` is what makes it go straight to Face ID on
      // the device in your hand.
      const { data, error } = await supabase.auth.passkey.startRegistration()
      if (error || !data) return error?.message ?? 'Could not start registration'

      const json = {
        ...data.options,
        authenticatorSelection: {
          ...(data.options as { authenticatorSelection?: object }).authenticatorSelection,
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          residentKey: 'required',
        },
      }

      let credential: PublicKeyCredential | null
      try {
        credential = (await navigator.credentials.create({
          publicKey: parseCreationOptions(json),
        })) as PublicKeyCredential | null
      } catch (e) {
        // A cancelled Face ID prompt lands here, and is not an error worth
        // shouting about.
        if (passkeyCancelled(e)) return CANCELLED
        return e instanceof Error ? e.message : String(e)
      }
      if (!credential) return 'No passkey was created'

      const { error: verifyError } = await supabase.auth.passkey.verifyRegistration({
        challengeId: data.challenge_id,
        credential: toJSON(credential),
      })
      return verifyError?.message ?? null
    },
    setup,
    linkError: LINK.error,
    async setPassword(password) {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) return error.message
      setSetup('passkey')
      return null
    },
    finishSetup() { setSetup(null) },
    async signOut() {
      const n = unsentChangeCount()
      if (n > 0 && unsent === null) { setUnsent(n); return }
      setUnsent(null)
      await supabase.auth.signOut()
    },
    async deleteAccount(confirmEmail) {
      if (!WORKER_URL) return 'VITE_WORKER_URL is not set'
      const { data: { session: current } } = await supabase.auth.getSession()
      const token = current?.access_token
      if (!token) return 'Not signed in'
      let res: Response
      try {
        res = await fetch(`${WORKER_URL}/me/delete`, {
          method: 'POST',
          headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
          body: JSON.stringify({ confirm: confirmEmail }),
        })
      } catch {
        return 'No connection. Nothing was deleted.'
      }
      if (res.status === 400) return 'That is not the email on this account. Nothing was deleted.'
      if (res.status === 403) return 'This account cannot be deleted from the app.'
      if (res.status !== 204) return `The account was not deleted (${res.status}). Try again.`
      // Not `signOut()` above: that waits on a "you have unsent changes" tap,
      // and the account those changes belong to no longer exists. `local`
      // because the session's server record went with the user — a global
      // sign-out would ask Auth to revoke something it can no longer find.
      // The SIGNED_OUT event is what clears this user's cache and queue
      // (`onAuthUser`, U0.4), the same path a normal sign-out takes.
      await supabase.auth.signOut({ scope: 'local' })
      return null
    },
  }

  return (
    <Ctx.Provider value={value}>
      {unsent !== null && (
        <Banner
          tone="warn"
          style={{ position: 'fixed', top: 'env(safe-area-inset-top, 0px)', left: 0, right: 0, zIndex: 1000 }}
          action={{ label: 'Sign out anyway', onClick: () => { void value.signOut() } }}
        >
          {unsent} unsent {unsent === 1 ? 'change' : 'changes'} will be lost
        </Banner>
      )}
      {children}
    </Ctx.Provider>
  )
}

export function useAuth() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth outside AuthProvider')
  return v
}
